/**
 * 75. Timesheets — weekly entry; approval; project allocation.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDate, formatQuantity } from "@/lib/money";

interface TimesheetRow {
  id: string;
  weekStarting: string;
  employee: string;
  projectCode: string;
  projectName: string;
  task: string;
  hours: string;
  billable: boolean;
  status: "Draft" | "Submitted" | "Approved" | "Rejected";
}

export function TimesheetsPage() {
  const { data, isLoading } = useApiList<TimesheetRow>(
    ["projects", "timesheets"],
    "/projects/timesheets",
  );

  return (
    <div>
      <PageHeader
        title="Timesheets"
        subtitle="Weekly entry, approval, and project allocation."
        actions={<Button variant="primary">New timesheet</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Timesheet entries (${data.total})`}>
          <DataTable<TimesheetRow>
            rows={data.items}
            columns={[
              {
                key: "weekStarting",
                label: "Week starting",
                render: (r) => formatDate(r.weekStarting),
              },
              { key: "employee", label: "Employee" },
              {
                key: "projectCode",
                label: "Project",
                render: (r) => (
                  <span className="atlas-mono">{r.projectCode}</span>
                ),
              },
              { key: "task", label: "Task" },
              {
                key: "hours",
                label: "Hours",
                numeric: true,
                render: (r) => formatQuantity(r.hours, 2),
              },
              {
                key: "billable",
                label: "Billable",
                render: (r) =>
                  r.billable ? (
                    <Badge tone="success">Yes</Badge>
                  ) : (
                    <Badge tone="default">No</Badge>
                  ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Approved" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Rejected" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "Submitted" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <Badge tone="default">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
